(() => {
  'use strict';
  document.documentElement.classList.add('js');
  const body = document.body;
  const root = body.dataset.root || '/';
  const enabled = body.dataset.savedEnabled === 'true';
  const storeKey = `playerboost:${location.host}:`;
  const memory = {};
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value);
  const cleanHandles = (values, max = 40) => Array.isArray(values) ? [...new Set(values.filter(validHandle))].slice(0, max) : [];
  function stored(key) {
    if (Object.prototype.hasOwnProperty.call(memory, key)) return memory[key];
    try { return cleanHandles(JSON.parse(localStorage.getItem(storeKey + key) || '[]')); }
    catch { return memory[key] || []; }
  }
  function saveList(key, values) {
    memory[key] = cleanHandles(values);
    try { localStorage.setItem(storeKey + key, JSON.stringify(memory[key])); } catch { /* Session-only fallback. */ }
    return memory[key];
  }
  function toggleInList(list, handle, max) {
    if (!validHandle(handle)) return { list, changed: false };
    if (list.includes(handle)) return { list: list.filter(x => x !== handle), changed: true };
    if (list.length >= max) return { list, changed: false };
    return { list: [...list, handle], changed: true };
  }
  function matchVariant(variants, options) {
    return variants.find(v => v.options.length === options.length && v.options.every((o, i) => o === options[i]));
  }
  // Small pure helpers also used by the automated tests.
  window.PlayerBoostTheme = Object.freeze({ cleanHandles, toggleInList, matchVariant });
  let toastTimer;
  function notify(message) {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message; el.hidden = false;
    clearTimeout(toastTimer); toastTimer = setTimeout(() => { el.hidden = true; }, 4500);
  }
  const parser = new DOMParser();
  function fragment(html, selector) { return parser.parseFromString(html, 'text/html').querySelector(selector); }
  async function getHTML(url, signal) {
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error('Content could not be loaded.');
    return response.text();
  }
  const productURL = (handle, view) => `${root}products/${encodeURIComponent(handle)}?view=${view}`;
  function modal(id) {
    const el = document.getElementById(id);
    if (!el || typeof el.showModal !== 'function') return null;
    document.querySelectorAll('dialog[open]').forEach(other => { if (other !== el) other.close(); });
    if (!el.open) el.showModal();
    return el;
  }
  function syncSaved() {
    const saved = stored('saved'), compared = stored('compare');
    document.querySelectorAll('[data-save]').forEach(b => b.setAttribute('aria-pressed', String(saved.includes(b.dataset.save))));
    document.querySelectorAll('[data-compare]').forEach(b => b.setAttribute('aria-pressed', String(compared.includes(b.dataset.compare))));
    document.querySelectorAll('[data-saved-count]').forEach(el => { el.textContent = saved.length; });
    document.querySelectorAll('[data-compare-count]').forEach(el => { el.textContent = compared.length; });
  }
  async function fetchCards(handles) {
    return Promise.all(handles.map(async handle => {
      try {
        const html = await getHTML(productURL(handle, 'service-card'));
        const content = fragment(html, '[data-service-fragment]');
        return { handle, content };
      } catch { return { handle, content: null }; }
    }));
  }
  function unavailableCard(handle, key) {
    const el = document.createElement('div'); el.className = 'product-card product-card-body';
    const p = document.createElement('p'); p.textContent = 'This service could not be loaded.';
    const a = document.createElement('a'); a.href = `${root}products/${encodeURIComponent(handle)}`; a.textContent = 'Open service →'; a.className = 'text-link';
    el.append(p, a);
    if (key) { const b = document.createElement('button'); b.type = 'button'; b.className = 'text-button'; b.textContent = 'Remove from list'; b.dataset[key] = handle; el.append(b); }
    return el;
  }
  let savedRequest = 0, compareRequest = 0, quickRequest = 0, recentRequest = 0;
  async function showSaved() {
    if (!enabled) return;
    const dialog = modal('SavedDialog'); if (!dialog) return;
    const target = dialog.querySelector('[data-saved-content]'); const request = ++savedRequest;
    const handles = stored('saved'); target.className = 'product-grid'; target.textContent = handles.length ? 'Loading your services…' : 'No saved services yet. Use the heart on a service to save it here.';
    const cards = await fetchCards(handles);
    if (request !== savedRequest || !handles.length) return;
    target.replaceChildren();
    cards.forEach(({ handle, content }) => { target.append(content?.querySelector('.product-card') || unavailableCard(handle, 'save')); });
    syncSaved();
  }
  async function showCompare() {
    if (!enabled) return;
    const dialog = modal('CompareDialog'); if (!dialog) return;
    const target = dialog.querySelector('[data-compare-content]'); const request = ++compareRequest;
    const handles = stored('compare').slice(0, 3); target.textContent = handles.length ? 'Loading comparison…' : 'Add up to three services using the compare button on a card.';
    const cards = await fetchCards(handles);
    if (request !== compareRequest || !handles.length) return;
    const table = document.createElement('table'); table.className = 'compare-table';
    const caption = document.createElement('caption'); caption.className = 'visually-hidden'; caption.textContent = 'Service comparison'; table.append(caption);
    const rows = ['Service', 'Price', 'Game', 'Delivery', 'Play mode', 'Platform', 'Availability'];
    rows.forEach((label, i) => {
      const tr = document.createElement('tr'); const th = document.createElement('th'); th.scope = 'row'; th.textContent = label; tr.append(th);
      cards.forEach(({ content }) => { const cell = document.createElement(i === 0 ? 'th' : 'td'); if (i === 0) cell.scope = 'col'; cell.textContent = content?.querySelectorAll('[data-compare-data] dd')[i]?.textContent || 'Unavailable'; tr.append(cell); });
      table.append(tr);
    });
    const actions = document.createElement('tr'); actions.append(document.createElement('td'));
    cards.forEach(({ handle, content }) => {
      const td = document.createElement('td'); const a = document.createElement('a'); a.href = content?.querySelector('[data-service-link]')?.getAttribute('href') || `${root}products/${encodeURIComponent(handle)}`; a.className = 'button'; a.textContent = 'View service ↗︎';
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'text-button'; remove.dataset.compare = handle; remove.textContent = 'Remove'; td.append(a, remove); actions.append(td);
    });
    table.append(actions); const wrap = document.createElement('div'); wrap.className = 'compare-scroll'; wrap.append(table); target.replaceChildren(wrap); syncSaved();
  }
  async function quickView(handle) {
    if (!validHandle(handle)) return;
    const dialog = modal('QuickDialog'); if (!dialog) { location.href = `${root}products/${handle}`; return; }
    const target = dialog.querySelector('[data-quick-content]'); const request = ++quickRequest; target.textContent = 'Loading service…';
    try {
      const html = await getHTML(productURL(handle, 'quick-view')); const content = fragment(html, '[data-quick-fragment]');
      if (request !== quickRequest) return;
      if (!content) throw new Error(); target.replaceChildren(content);
    } catch { if (request === quickRequest) target.replaceChildren(unavailableCard(handle)); }
  }
  async function recentServices() {
    if (!enabled) return;
    const containers = document.querySelectorAll('[data-recent-section]'); if (!containers.length) return;
    const current = document.querySelector('[data-current-handle]')?.dataset.currentHandle;
    const handles = stored('recent').filter(h => h !== current).slice(0, 8); const request = ++recentRequest;
    containers.forEach(el => { el.hidden = !handles.length; }); if (!handles.length) return;
    const cards = await fetchCards(handles); if (request !== recentRequest) return;
    containers.forEach(el => { const target = el.querySelector('[data-recent-content]'); target.replaceChildren(); cards.forEach(({ content }) => { const card = content?.querySelector('.product-card'); if (card) target.append(card.cloneNode(true)); }); el.hidden = !target.children.length; });
    containers.forEach(el => {
      const row = el.querySelector('[data-recent-content]');
      if (el.querySelector('.pb-recent-controls')) { row.dispatchEvent(new Event('scroll')); return; }
      const controls = document.createElement('div');
      controls.className = 'pb-recent-controls';
      controls.innerHTML = '<button class="pb-carousel-hint pb-carousel-hint--previous" type="button" aria-label="Previous recently viewed products"><span aria-hidden="true">←</span> Previous</button><button class="pb-carousel-hint" type="button" aria-label="More recently viewed products">More <span aria-hidden="true">→</span></button>';
      const frame = document.createElement('div');
      frame.className = 'pb-carousel-frame';
      row.before(frame);
      frame.append(row, controls);
      const [previous, next] = controls.children;
      const update = () => {
        previous.hidden = row.scrollLeft < 2;
        next.hidden = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
        controls.hidden = row.scrollWidth <= row.clientWidth + 2;
      };
      const move = direction => row.scrollBy({left: direction * row.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      row.addEventListener('scroll', update, {passive:true});
      new ResizeObserver(update).observe(row);
      requestAnimationFrame(update);
    });
    syncSaved();
  }
  let cartBusy = false;
  function renderCart(html) {
    const content = fragment(html, '[data-cart-drawer-content]'); const target = document.querySelector('#CartDialog');
    if (!content || !target) throw new Error('Open your full cart to review your order.');
    target.replaceChildren(content);
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = content.dataset.itemCount; });
    return target;
  }
  async function refreshCart() {
    const url = `${root}?sections=cart-drawer`;
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh your cart.');
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error('Open your full cart to review your order.');
    return renderCart(sections['cart-drawer']);
  }
  async function openCart() {
    if (body.dataset.ajaxCart !== 'true' || !modal('CartDialog')) { location.href = `${root}cart`; return; }
    try { await refreshCart(); }
    catch { notify('Could not refresh the cart. Use View full cart to check your latest items.'); }
  }
  async function changeCart(key, quantity) {
    if (cartBusy || !Number.isInteger(quantity) || quantity < 0) return;
    cartBusy = true; const dialog = document.querySelector('#CartDialog'); dialog.setAttribute('aria-busy', 'true');
    dialog.querySelectorAll('button,input').forEach(el => { el.disabled = true; });
    try {
      const response = await fetch(`${root}cart/change.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: root }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This quantity is not available.');
      if (data.sections?.['cart-drawer']) renderCart(data.sections['cart-drawer']); else await refreshCart();
      dialog.querySelector('[data-close-dialog]')?.focus();
    } catch (error) { const target = dialog.querySelector('[data-cart-error]'); target.textContent = `${error.message} Check your full cart before retrying.`; target.hidden = false; }
    finally { cartBusy = false; dialog.removeAttribute('aria-busy'); dialog.querySelectorAll('button,input').forEach(el => { el.disabled = false; }); }
  }
  function initProduct(section) {
    if (section.dataset.bound) return; section.dataset.bound = 'true';
    const form = section.querySelector('.product-form'), select = section.querySelector('[data-variant-select]');
    if (!form || !select) return;
    let variants;
    try { variants = JSON.parse(section.querySelector('[data-variant-data]').textContent); } catch { return; }
    const pickers = [...section.querySelectorAll('[data-product-option]')];
    const pickerWrap = section.querySelector('[data-option-picker]');
    if (pickers.length && variants.length) { pickerWrap.hidden = false; section.querySelector('[data-variant-fallback]').hidden = true; }
    const buttons = [section.querySelector('[data-add-button]'), section.querySelector('[data-sticky-buy]')].filter(Boolean);
    let submitting = false;
    function update(resetQuantity = false) {
      section.querySelectorAll('[data-card-option]').forEach(card => { card.checked = pickers[Number(card.dataset.cardOption)]?.value === card.value; });
      const v = pickers.length ? matchVariant(variants, pickers.map(p => p.value)) : variants.find(v => String(v.id) === select.value);
      select.value = v ? String(v.id) : '';
      section.querySelector('[data-variant-price]').textContent = v?.price || 'Not available';
      const compare = section.querySelector('[data-compare-price]'); compare.textContent = v?.compare || ''; compare.hidden = !v?.compare;
      const sticky = section.querySelector('[data-sticky-price]'); if (sticky) sticky.textContent = v?.price || 'Not available';
      buttons.forEach(b => { b.disabled = submitting || !v?.available; b.textContent = submitting ? 'Adding…' : v?.available ? 'Add to cart →' : 'Unavailable'; });
      section.querySelector('[data-variant-error]').hidden = Boolean(v);
      const qty = form.querySelector('[name=quantity]');
      if (v && qty) { qty.min = v.min; qty.step = v.step; if (v.max) qty.max = v.max; else qty.removeAttribute('max'); if (resetQuantity) qty.value = v.min; }
      section.querySelectorAll('[data-rank-start]').forEach(el => { el.textContent = pickers[0]?.value || v?.options[0] || '—'; });
      section.querySelectorAll('[data-rank-target]').forEach(el => { el.textContent = pickers[1]?.value || v?.options[1] || '—'; });
      const summary = section.querySelector('[data-selection-summary]'); summary.textContent = v ? v.options.filter(o => o !== 'Default Title').join(' / ') : 'Choose an available combination';
      const url = new URL(location.href); if (v) url.searchParams.set('variant', String(v.id)); else url.searchParams.delete('variant'); history.replaceState({}, '', url);
      return v;
    }
    const initial = variants.find(v => String(v.id) === select.value);
    if (initial) pickers.forEach((picker, i) => { picker.value = initial.options[i]; });
    pickers.forEach(p => p.addEventListener('change', () => update(true)));
    section.querySelectorAll('[data-card-option]').forEach(card => card.addEventListener('change', () => { if (!card.checked) return; const picker = pickers[Number(card.dataset.cardOption)]; if (picker) { picker.value = card.value; update(true); } }));
    select.addEventListener('change', () => { const v = variants.find(v => String(v.id) === select.value); if (v) pickers.forEach((p, i) => { p.value = v.options[i]; }); update(true); });
    update();
    if (enabled && validHandle(section.dataset.currentHandle)) saveList('recent', [section.dataset.currentHandle, ...stored('recent').filter(x => x !== section.dataset.currentHandle)].slice(0, 8));
    if (body.dataset.ajaxCart !== 'true') return;
    form.addEventListener('submit', async e => {
      if (e.submitter && e.submitter.name !== 'add' && !e.submitter.hasAttribute('data-sticky-buy')) return;
      e.preventDefault(); if (submitting || !form.reportValidity()) return;
      const v = update(); if (!v?.available) return;
      const error = section.querySelector('[data-product-error]'); error.hidden = true;
      const data = new FormData(form); data.set('sections', 'cart-drawer'); data.set('sections_url', root);
      submitting = true; update();
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', body: data });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'This service could not be added.');
        if (result.sections?.['cart-drawer']) renderCart(result.sections['cart-drawer']); else await refreshCart();
        modal('CartDialog'); notify('Added to your game plan.');
      } catch (err) { error.textContent = `${err.message} Check your cart before trying again.`; error.hidden = false; }
      finally { submitting = false; update(); }
    });
  }
  function initSearch(wrapper) {
    if (wrapper.dataset.bound) return; wrapper.dataset.bound = 'true';
    if (wrapper.closest('.site-header') && !wrapper.closest('dialog')) {
      const dialog = document.createElement('dialog');
      dialog.className = 'pb-search-modal';
      dialog.setAttribute('aria-label', 'Find your next boost');
      dialog.innerHTML = '<div class="pb-search-modal-heading"><h2>Find your next boost.</h2><button type="button" aria-label="Close search"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m6 6 12 12M18 6 6 18"/></svg></button></div><p>Search games and services, or explore a popular pick.</p>';
      const modalSearch = wrapper.cloneNode(true);
      delete modalSearch.dataset.bound;
      modalSearch.querySelectorAll('[id]').forEach(el => { el.id += '-modal'; });
      modalSearch.querySelector('label[for]')?.setAttribute('for', modalSearch.querySelector('input').id);
      modalSearch.querySelector('input').setAttribute('aria-controls', modalSearch.querySelector('[data-search-results]').id);
      modalSearch.querySelector('input').setAttribute('aria-haspopup', 'dialog');
      modalSearch.querySelector('[data-search-results]').setAttribute('role', 'dialog');
      modalSearch.querySelector('[data-search-results]').setAttribute('aria-label', 'Search suggestions');
      modalSearch.querySelector('input').placeholder = 'Try a game or rank boosting…';
      dialog.append(modalSearch);
      document.body.append(dialog);
      initSearch(modalSearch);
      const trigger = wrapper.querySelector('[data-search-input]');
      let restoringFocus = false;
      const open = () => {
        if (restoringFocus || dialog.open) return;
        dialog.showModal();
        trigger.setAttribute('aria-expanded', 'true');
        const field = modalSearch.querySelector('input');
        field.value = trigger.value;
        field.focus();
        field.dispatchEvent(new Event('input'));
      };
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.removeAttribute('aria-autocomplete');
      dialog.id = trigger.id + '-dialog';
      trigger.setAttribute('aria-controls', dialog.id);
      trigger.addEventListener('focus', open);
      trigger.addEventListener('click', open);
      wrapper.querySelector('form').addEventListener('submit', event => { event.preventDefault(); open(); });
      const closeModal = () => { restoringFocus = true; dialog.close(); };
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeModal(); });
      dialog.querySelector('.pb-search-modal-heading button').addEventListener('click', closeModal);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeModal();
      });
      dialog.addEventListener('close', () => { restoringFocus = true; trigger.setAttribute('aria-expanded', 'false'); trigger.focus(); setTimeout(() => { restoringFocus = false; }, 0); });
      return;
    }
    const input = wrapper.querySelector('[data-search-input]'), output = wrapper.querySelector('[data-search-results]');
    let timer, controller, sequence = 0;
    const normalizeGame = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
    function wordDistance(a,b) {
      let row = Array.from({length:b.length+1},(_,i)=>i);
      for(let i=1;i<=a.length;i++){const next=[i];for(let j=1;j<=b.length;j++)next[j]=Math.min(next[j-1]+1,row[j]+1,row[j-1]+(a[i-1]===b[j-1]?0:1));row=next;}return row[b.length];
    }
    function gameScore(name,query) {
      const n=normalizeGame(name),q=normalizeGame(query);
      if(n===q)return 0;if(n.includes(q))return 1;
      const words=n.split(' ');let score=2;
      for(const word of q.split(' ')){
        const distance=Math.min(...words.map(target=>target.startsWith(word)?0:wordDistance(word,target)));
        if(distance>(word.length<4?0:word.length<8?1:2))return Infinity;
        score+=distance;
      }return score;
    }
    async function findGames(query) {
      let source=document;
      if(!source.querySelector('[data-pb-catalog] > .pb-compact-games > a')){
        const home=document.querySelector('[data-tile-game-menu]')?.dataset.homeUrl || window.Shopify?.routes?.root || '/';
        window.pbSearchCatalogPromise ||= fetch(home,{credentials:'same-origin'}).then(r=>{if(!r.ok)throw new Error();return r.text()}).then(html=>new DOMParser().parseFromString(html,'text/html')).catch(()=>{window.pbSearchCatalogPromise=null;return null});
        source=await window.pbSearchCatalogPromise;
      }
      const entries=new Map();
      source?.querySelectorAll('[data-pb-catalog] > .pb-compact-games > a').forEach(tile=>{
        const name=tile.querySelector('strong')?.textContent.trim();
        if(name)entries.set(normalizeGame(name),{name,node:tile});
      });
      wrapper.querySelector('[data-search-vendors]')?.content.querySelectorAll('a').forEach(link=>{
        const name=link.textContent.trim(),key=normalizeGame(name);
        if(!entries.has(key))entries.set(key,{name,node:link});
      });
      return [...entries.values()].map(entry=>({...entry,score:gameScore(entry.name,query)})).filter(entry=>Number.isFinite(entry.score)).sort((a,b)=>a.score-b.score).slice(0,6);
    }

    wrapper.addEventListener('click', event => {
      const suggestion = event.target.closest('.pb-search-discovery > div > a.search-result');
      if (!suggestion) return;
      event.preventDefault();
      const url = new URL(suggestion.href);
      input.value = url.searchParams.get('q') || suggestion.textContent.trim();
      input.focus(); input.dispatchEvent(new Event('input'));
    });
    function discovery(){const template=wrapper.querySelector('[data-search-discovery]');if(!template){close();return;}output.replaceChildren(template.content.cloneNode(true));
let fallback=output.querySelector('[data-search-game-fallback]');
if(!fallback){
 const aside=output.querySelector('.pb-search-discovery aside');
 if(aside && !aside.querySelector('a')){fallback=document.createElement('div');fallback.dataset.searchGameFallback='';aside.append(fallback);}
}
if(fallback){
 const populate=doc=>{
  if(!fallback.isConnected)return false;
  const tiles=[...doc.querySelectorAll('[data-pb-catalog] > .pb-compact-games > .pb-preview-game-tile')].slice(0,6);
  if(!tiles.length)return false;
  fallback.replaceChildren(...tiles.map(tile=>{const a=tile.cloneNode(true);a.hidden=false;a.removeAttribute('id');a.removeAttribute('data-shopify-editor-block');return a}));
  return true;
 };
 if(!populate(document)){
  fallback.textContent='Loading games…';
  const home=document.querySelector('[data-tile-game-menu]')?.dataset.homeUrl || window.Shopify?.routes?.root || '/';
  window.pbSearchCatalogPromise ||= fetch(home,{credentials:'same-origin'}).then(r=>{if(!r.ok)throw new Error('Catalog unavailable');return r.text()}).then(html=>new DOMParser().parseFromString(html,'text/html')).catch(()=>{window.pbSearchCatalogPromise=null;return null});
  window.pbSearchCatalogPromise.then(doc=>{if(!fallback.isConnected)return;if(!doc || !populate(doc)){fallback.replaceChildren();const link=document.createElement('a');link.href=home+'#x-games';link.textContent='Browse all games →';fallback.append(link);}});
 }
}
output.hidden=false;input.setAttribute('aria-expanded','true');}
    input.addEventListener('focus',()=>{if(input.value.trim().length<2)discovery();else input.dispatchEvent(new Event('input'));});
    function close() { output.hidden = true; input.setAttribute('aria-expanded', 'false'); }
    input.addEventListener('input', () => {
      clearTimeout(timer); controller?.abort(); const term = input.value.trim(); const request = ++sequence;
      if (term.length < 2) { discovery(); return; }
      timer = setTimeout(async () => {
        controller = new AbortController();
        try {
          const gameMatches = await findGames(term);
          if (request !== sequence) return;
          const corrected = gameMatches[0]?.score >= 2 ? gameMatches[0].name : term;
          const params = new URLSearchParams({ q: corrected, 'resources[type]': 'product,collection', 'resources[limit]': wrapper.dataset.resultLimit || '6', section_id: 'predictive-search' });
          const html = await getHTML(`${root}search/suggest?${params}`, controller.signal);
          if (request !== sequence) return;
          const content = fragment(html, '[data-predictive-content]'); if (!content) { close(); return; }
          const columns = document.createElement('div'); columns.className = 'pb-search-discovery';
          const services = document.createElement('div'), games = document.createElement('aside');
          services.innerHTML = '<h3>Matching services</h3>'; games.innerHTML = '<h3>Matching games</h3>';
          content.querySelectorAll('a.search-result').forEach(link => {
            (link.querySelector('small')?.textContent === 'Game collection' ? games : services).append(link);
          });
          const seenGames = new Set([...games.querySelectorAll('a')].map(a=>a.href));
          gameMatches.forEach(({name,node})=>{
            if(seenGames.has(node.href))return;
            const link=node.cloneNode(true);link.hidden=false;link.removeAttribute('id');link.removeAttribute('data-shopify-editor-block');
            if(!link.classList.contains('pb-preview-game-tile')){link.className='search-result';link.textContent=name;}
            games.append(link);seenGames.add(node.href);
          });
          for (const [panel, message] of [[services,'No matching services. Try a shorter search.'],[games,'No matching games.']]) {
            if (!panel.querySelector('a')) { const empty = document.createElement('p'); empty.textContent = message; panel.append(empty); }
          }
          columns.append(services,games);
          const all = content.querySelector('.search-all');
          output.replaceChildren(columns); if (all) output.append(all);
          output.hidden = false; input.setAttribute('aria-expanded', 'true');
        } catch { if (request === sequence) close(); }
      }, 220);
    });
    wrapper.addEventListener('keydown', e => {
      const links = [...output.querySelectorAll('a')];
      if (e.key === 'Escape') { clearTimeout(timer);controller?.abort();sequence++;input.focus();close(); }
      if (e.key === 'ArrowDown' && !output.hidden) { e.preventDefault(); const index = links.indexOf(document.activeElement); links[(index + 1) % links.length]?.focus(); }
      if (e.key === 'ArrowUp' && !output.hidden) { e.preventDefault(); const index = links.indexOf(document.activeElement); if (index <= 0) input.focus(); else links[index - 1]?.focus(); }
    });
    wrapper.addEventListener('focusout', () => setTimeout(() => { if (!wrapper.contains(document.activeElement)) close(); }, 0));
  }
  function initDirectory(section) {
    if (section.dataset.bound) return; section.dataset.bound = 'true';
    const cards = [...section.querySelectorAll('[data-game-name]')]; let active = 'all';
    const search = section.querySelector('[data-game-search]'), genres = section.querySelector('[data-genres]');
    [...new Set(cards.map(c => c.dataset.gameGenre).filter(Boolean))].forEach(genre => { const b = document.createElement('button'); b.type = 'button'; b.className = 'chip'; b.dataset.genre = genre; b.textContent = genre; b.setAttribute('aria-pressed', 'false'); genres.append(b); });
    function filter() {
      const term = search.value.trim().toLowerCase(); let count = 0;
      cards.forEach(card => { card.hidden = !(card.dataset.gameName.toLowerCase().includes(term) && (active === 'all' || active === card.dataset.gameGenre)); if (!card.hidden) count++; });
      section.querySelector('[data-game-empty]').hidden = count !== 0; section.querySelector('[data-game-status]').textContent = `${count} games shown`;
    }
    search.addEventListener('input', filter);
    genres.addEventListener('click', e => { const b = e.target.closest('[data-genre]'); if (!b) return; active = b.dataset.genre; genres.querySelectorAll('button').forEach(el => { el.classList.toggle('active', el === b); el.setAttribute('aria-pressed', String(el === b)); }); filter(); });
  }
  function initTabs(section) {
    if (section.dataset.bound) return; section.dataset.bound = 'true';
    const tabs = [...section.querySelectorAll('[data-service-tab]')], panels = [...section.querySelectorAll('[data-service-panel]')];
    function activate(tab) { tabs.forEach(t => { t.setAttribute('aria-selected', String(t === tab)); t.tabIndex = t === tab ? 0 : -1; }); panels.forEach(p => { p.hidden = p.dataset.servicePanel !== tab.dataset.serviceTab; }); }
    tabs.forEach((tab, index) => { tab.addEventListener('click', () => activate(tab)); tab.addEventListener('keydown', e => { let next; if (e.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length]; if (e.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length]; if (e.key === 'Home') next = tabs[0]; if (e.key === 'End') next = tabs[tabs.length - 1]; if (next) { e.preventDefault(); activate(next); next.focus(); } }); });
    if (tabs.length) activate(tabs[0]);
  }
  function initFAQ(section) {
    if (section.dataset.bound) return; section.dataset.bound = 'true';
    const input = section.querySelector('[data-faq-input]'), items = [...section.querySelectorAll('details')];
    if (!input) return;
    input.addEventListener('input', () => { const term = input.value.trim().toLowerCase(); let count = 0; items.forEach(el => { el.hidden = !el.textContent.toLowerCase().includes(term); if (!el.hidden) count++; }); section.querySelector('[data-faq-empty]').hidden = count > 0; section.querySelector('[data-faq-status]').textContent = `${count} questions found`; });
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-product-section]').forEach(initProduct);
    scope.querySelectorAll('[data-live-search]').forEach(initSearch);
    scope.querySelectorAll('[data-game-directory]').forEach(initDirectory);
    scope.querySelectorAll('[data-service-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-faq-search]').forEach(initFAQ);
    syncSaved();
    // Recently viewed is below the page content; fetch cards as it approaches.
    const recent = [...scope.querySelectorAll('[data-recent-section]')];
    if (scope === document && 'IntersectionObserver' in window && recent.length) {
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect(); recentServices();
      }, {rootMargin: '800px 0px'});
      recent.forEach(section => observer.observe(section.parentElement));
    } else recentServices();
  }
  document.addEventListener('click', e => {
    const button = e.target.closest('button,a');
    if (!button) return;
    if (button.hasAttribute('data-close-dialog')) button.closest('dialog')?.close();
    if (button.hasAttribute('data-open-cart') && body.dataset.ajaxCart === 'true') { e.preventDefault(); openCart(); }
    if (button.hasAttribute('data-open-saved')) showSaved();
    if (button.hasAttribute('data-open-compare')) showCompare();
    if (button.dataset.quick) quickView(button.dataset.quick);
    if (button.dataset.save && enabled) {
      const result = toggleInList(stored('saved'), button.dataset.save, 40); if (!result.changed) { notify('You can save up to 40 services.'); return; }
      saveList('saved', result.list); syncSaved();
      notify(result.list.includes(button.dataset.save) ? 'Service saved on this device.' : 'Service removed from saved.');
      if (document.querySelector('#SavedDialog[open]')) showSaved();
    }
    if (button.dataset.compare && enabled) {
      const result = toggleInList(stored('compare').slice(0, 3), button.dataset.compare, 3); if (!result.changed) { notify('Compare up to three services. Remove one to add another.'); return; }
      saveList('compare', result.list); syncSaved(); notify(result.list.includes(button.dataset.compare) ? 'Added to comparison.' : 'Removed from comparison.');
      if (document.querySelector('#CompareDialog[open]')) showCompare();
    }
    if (button.hasAttribute('data-clear-compare')) { saveList('compare', []); syncSaved(); showCompare(); }
    if (button.hasAttribute('data-clear-recent')) { saveList('recent', []); recentServices(); }
    if (button.dataset.cartRemove) changeCart(button.dataset.cartRemove, 0);
  });
  document.addEventListener('change', e => { if (e.target.matches('[data-cart-quantity]')) changeCart(e.target.dataset.cartQuantity, Number(e.target.value)); });
  document.addEventListener('keydown', e => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); document.querySelector('[data-search-input]')?.focus(); }
    if (e.key === 'Escape') document.querySelectorAll('.mega-menu[open],.filter[open]').forEach(el => { el.open = false; el.querySelector('summary')?.focus(); });
  });
  window.addEventListener('storage', e => { if (e.key?.startsWith(storeKey)) { delete memory[e.key.slice(storeKey.length)]; syncSaved(); } });
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:block:select', e => { const panel = e.target.closest('[data-service-panel]'); if (panel) document.querySelector(`[data-service-tab="${panel.dataset.servicePanel}"]`)?.click(); });
  document.querySelectorAll('dialog').forEach(el => el.addEventListener('click', e => { if (e.target !== el) return; const box = el.getBoundingClientRect(); if (e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom) el.close(); }));
  init();
})();

// Rotate only the search hint; typed queries and accessible labels stay unchanged.
(() => {
  const initialized = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function initializeSearchHints() {
    document.querySelectorAll('[data-search-input]').forEach(input => {
      if (initialized.has(input)) return;
      initialized.add(input);
      const words = ['games', 'boosts', 'coaching', 'ranks'];
      let word = 0, length = words[0].length, deleting = true, timer;
      const render = () => { input.placeholder = 'Search for ' + words[word].slice(0, length); };
      const pause = () => {
        clearTimeout(timer);
        input.placeholder = 'Search for games, boosts, coaching…';
      };
      const tick = () => {
        if (!input.isConnected) { clearTimeout(timer); return; }
        if (document.hidden || reducedMotion.matches || document.activeElement === input || input.value) { pause(); return; }
        if (deleting) {
          length--;
          if (length === 0) { deleting = false; word = (word + 1) % words.length; }
        } else {
          length++;
          if (length === words[word].length) deleting = true;
        }
        render();
        timer = setTimeout(tick, deleting && length === words[word].length ? 2200 : deleting ? 55 : 95);
      };
      const resume = () => {
        pause();
        if (!input.isConnected || document.hidden || reducedMotion.matches || document.activeElement === input || input.value) return;
        length = words[word].length; deleting = true; render();
        timer = setTimeout(tick, 2400);
      };
      input.addEventListener('focus', pause);
      input.addEventListener('blur', resume);
      input.addEventListener('input', () => { if (input.value) pause(); });
      const visibility = () => {
        if (!input.isConnected) {
          clearTimeout(timer);
          document.removeEventListener('visibilitychange', visibility);
          reducedMotion.removeEventListener('change', visibility);
          return;
        }
        resume();
      };
      document.addEventListener('visibilitychange', visibility);
      reducedMotion.addEventListener('change', visibility);
      resume();
    });
  }
  initializeSearchHints();
  document.addEventListener('shopify:section:load', initializeSearchHints);
})();

// Delegated protection also covers cards loaded into Saved and search results.
document.addEventListener('dragstart', event => {
  if (!(event.target instanceof Element)) return;
  if (event.target.matches('.product-card img, .product-stage img') || event.target.closest('.product-card .pb-card-cover')) event.preventDefault();
});

document.addEventListener('input',event=>{if(!event.target.matches('[data-game-menu-search]'))return;const panel=event.target.closest('.pb-mockup-game-menu');const query=event.target.value.trim().toLowerCase();let count=0;panel.querySelectorAll('.pb-game-menu-grid>a').forEach(link=>{link.hidden=!link.textContent.toLowerCase().includes(query);if(!link.hidden)count++});panel.querySelector('[data-game-menu-empty]').hidden=count>0});
document.addEventListener('click',event=>{const button=event.target.closest('[data-game-menu-close]');if(button){const menu=button.closest('details');menu.open=false;menu.querySelector('summary').focus()}});

// Close the Games dropdown on an outside click, without stealing focus.
document.addEventListener('pointerdown', event => {
  document.querySelectorAll('.mega-menu[open]').forEach(menu => {
    if (event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
  });
});
